import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Icon, Popup } from "semantic-ui-react";

import { ConditionalWrap, DATE_FORMATS_FNS, DatePicker, Editable } from "components";
import { Element } from "components/ReactScroll/index";
import { CUSTOMER_TYPES, DMS_CAPABILITIES } from "models";
import { CopyContent } from "modules/AppointmentDetails/components";
import "modules/AppointmentDetails/components/CarDetails/CarDetails.scss";
import { useTextOverflow } from "modules/AppointmentDetails/components/CarDetails/hooks";
import { useAppointmentLocation, useLiveUsers } from "modules/AppointmentDetails/hooks";
import { CustomerInfo } from "modules/HoldingAppointmentDetails/components/CarDetails/components";
import { classNames, getUserNameInitials } from "utils/common";
import { formatDate, toDate } from "utils/dateHelperFunctions";

import type { SectionMenu } from "components";
import type { Appointment } from "models";

type CarDetailsProps = {
  isExpanded: boolean;
  appointment: Appointment;
  sectionMenu: SectionMenu;
};

export const CarDetails = ({ isExpanded, appointment, sectionMenu }: CarDetailsProps) => {
  const { t } = useTranslation();

  const [showCardDetails, setShowCardDetails] = useState(isExpanded);

  const appointmentLocation = useAppointmentLocation();
  const { users } = useLiveUsers(appointment.id);

  useEffect(() => {
    setShowCardDetails(isExpanded);
  }, [isExpanded]);

  const toggleCardDetails = () => setShowCardDetails(p => !p);

  const nextDate = appointment.next_date && !appointment.next_date.startsWith("0001-01-01T00:00:00") ? toDate(appointment.next_date) : null;

  const carMakeModel = `${appointment.car?.make || ""} ${appointment.car?.model || ""}`.trim();
  const carMakeModelRef = useRef<HTMLSpanElement>(null);
  const hasCarMakeModelOverflow = useTextOverflow(carMakeModelRef);

  return (
    <Element className="DetailsCards-container" id={sectionMenu.id}>
      <div className="row">
        <Icon className={sectionMenu.icon} />
        <h3>{sectionMenu.title}</h3>
        <Icon className={`chevron ${showCardDetails ? "down" : "right"} pointer`} onClick={toggleCardDetails} />
        <div className="full end">
          {users.map((user, index) => {
            const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
            return (
              <Popup
                key={index}
                content={fullName}
                trigger={
                  user.profile_picture ? (
                    <img src={user.profile_picture} className="active-user" />
                  ) : (
                    <div className="active-user" key={index}>
                      {getUserNameInitials(fullName)}
                    </div>
                  )
                }
              />
            );
          })}
        </div>
      </div>
      <div className="CarDetails">
        <div className="car-image-container">
          {appointment.car?.profile_picture ? <img src={appointment.car.profile_picture} className="car-image" /> : <Icon className="image i-grey" size="huge" />}
        </div>
        <div className="car-container">
          <div className="car-header">
            <div className="car-items">
              <Icon className="car mirrors" />
              <p>{t("v8_car", "Car")}</p>

              <div className="car-items-reg-info ">
                {appointment.car?.reg_nr && (
                  <CopyContent content={appointment.car.reg_nr}>
                    <strong>{appointment.car.reg_nr}</strong>
                  </CopyContent>
                )}

                <Popup
                  disabled={!hasCarMakeModelOverflow}
                  content={
                    <>
                      <span>
                        <strong>{t("v8_make", "Make")}:</strong> {appointment.car?.make || "-"}
                      </span>
                      <br />
                      <span>
                        <strong>{t("v8_model", "Model")}:</strong> {appointment.car?.model || "-"}
                      </span>
                    </>
                  }
                  trigger={
                    <span ref={carMakeModelRef} className="car-items-make-model ellipsis">
                      <strong>{carMakeModel}</strong>
                    </span>
                  }
                />
              </div>
            </div>
          </div>
          <div className="car-info">
            <div className="info-data">
              <p>{t("v8_vin_chassis", "Vin / Chassis") + " #"}</p>
              <CopyContent content={appointment.car?.vin_nr ?? ""}>
                <p className="car-info-value">{appointment.car?.vin_nr}</p>
              </CopyContent>
            </div>

            {appointment.car?.warranty_date_end && (
              <div className="info-data">
                <p>{t("v8_warranty_end_date", "Warranty End ")}</p>
                <p className="car-info-value">{formatDate(appointment.car.warranty_date_end, DATE_FORMATS_FNS.dateMonthYear)}</p>
              </div>
            )}

            {appointment.car?.dms_registration && (
              <div className="info-data">
                <p>{t("v8_dms_registration", "DMS registration")}</p>
                <p className="car-info-value">{formatDate(appointment.car.dms_registration, DATE_FORMATS_FNS.dateMonthYear)}</p>
              </div>
            )}

            {appointment.car?.rdw_first_registration && (
              <div className="info-data">
                <p>{t("v8_first_registration_date", "First Registration")}</p>
                <p className="car-info-value">{formatDate(appointment.car.rdw_first_registration, DATE_FORMATS_FNS.dateMonthYear)}</p>
              </div>
            )}

            {appointment.car?.rdw_first_registration_nl && (
              <div className="info-data">
                <p>{t("v8_first_registration_date_nl", "First registration NL")}</p>
                <p className="car-info-value">{formatDate(appointment.car.rdw_first_registration_nl, DATE_FORMATS_FNS.dateMonthYear)}</p>
              </div>
            )}

            {appointment.fleet_number && (
              <div className="info-data">
                <p>{t("v8_fleet_number", "Fleet Number")}</p>
                <p className="car-info-value">{appointment.fleet_number}</p>
              </div>
            )}

            <div className="info-data">
              <p>{t("v8_current_km", "Current Km")}</p>
              <ConditionalWrap
                condition={Boolean(appointment.current_km)}
                wrap={children => <CopyContent content={String(appointment.current_km)}>{children}</CopyContent>}
              >
                <Editable isNumberInput valueClassName="car-info-value" value={String(appointment.current_km)} disabled />
              </ConditionalWrap>
            </div>

            {appointmentLocation?.apk_visible && (
              <div className="info-data">
                <p>{t("v8_apk", "APK")}</p>
                {appointment.car?.apk_date_dms && <p className="car-info-value">{formatDate(appointment.car.apk_date_dms, DATE_FORMATS_FNS.dateMonthYear)}</p>}
              </div>
            )}

            {!appointmentLocation?.hide_next_km_and_next_date && (
              <div className="info-data">
                <p>{t("v8_next_km", "Next Km")}</p>
                <Editable isNumberInput valueClassName="car-info-value" value={String(appointment.next_km)} disabled />
              </div>
            )}

            {!appointmentLocation?.hide_next_km_and_next_date && (
              <div className="info-data AppointmentDate">
                <p>{t("v8_next_date", "Next Date")}</p>
                {nextDate ? <DatePicker date={nextDate} withPortal showMonthDropdown showYearDropdown disabled /> : <p className="car-info-value"></p>}
              </div>
            )}

            {appointmentLocation?.dms_capability_ids?.includes(DMS_CAPABILITIES.ImportMileage) && (
              <div className="info-column">
                <p>{t("v8_dms_km", "DMS Km")}</p>
                <p className="car-info-value">{appointment.dms_km}</p>
              </div>
            )}

            {appointment.car?.engine_number && (
              <div className="info-column">
                <p>{t("v8_engine", "Engine") + " #"}</p>
                <p className="car-info-value">{appointment.car.engine_number}</p>
              </div>
            )}
          </div>

          <div className={`customer-details ${classNames({ hide: !showCardDetails })}`}>
            <div className="car-header">
              <div className="car-items">
                <Icon className="user person" />
                <p>{t("v8_customer", "Customer")}</p>
              </div>
            </div>
            <div className="customer-details-info">
              <div className="info-data-container">
                {appointmentLocation?.dp_driver_visible && (
                  <div className="info-data">
                    <div>
                      <CustomerInfo appointment={appointment} customerType={CUSTOMER_TYPES.Driver} />
                    </div>
                  </div>
                )}
                {appointmentLocation?.dp_contractor_visible && (
                  <div className="info-data">
                    <div>
                      <CustomerInfo appointment={appointment} customerType={CUSTOMER_TYPES.Contractor} />
                    </div>
                  </div>
                )}
                {appointmentLocation?.dp_owner_visible && (
                  <div className="info-data">
                    <div>
                      <CustomerInfo appointment={appointment} customerType={CUSTOMER_TYPES.Owner} />
                    </div>
                  </div>
                )}
              </div>
              {appointment.customer_owner_name && (
                <Dropdown
                  disabled
                  className="selection"
                  trigger={
                    <div className="customer-owner-container">
                      <div className="customer-owner-icon">CO</div>
                      <div>
                        <span className="customer-owner-name-text">{appointment.customer_owner_name}</span>
                      </div>
                    </div>
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Element>
  );
};
